import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { WavelengthParams } from './wavelength.model';

/**
 * Creates wavelength visualization for sine/cosine functions.
 *
 * Features:
 * - Horizontal double-arrow line from peak to peak (one complete wavelength)
 *
 * For sine: a * sin(b(x - c)) + d
 * - First max at x = c + π/(2b)
 * - Second max at x = c + π/(2b) + 2π/b
 *
 * For cosine: a * cos(b(x - c)) + d
 * - First max at x = c (when a > 0)
 * - Second max at x = c + 2π/b
 */
export class WavelengthCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'wavelength';
  override readonly computedValueKeys = ['value'] as const;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as WavelengthParams | undefined;

    if (!params?.of) {
      throw new Error('Wavelength feature requires params.of to specify the parent function');
    }

    const parentElement = context.elements.get(params.of);
    if (!parentElement) {
      throw new Error(`Parent element ${params.of} not found`);
    }

    if (parentElement.type !== 'sine' && parentElement.type !== 'cosine') {
      throw new Error(`Wavelength feature only supports sine and cosine functions, got ${parentElement.type}`);
    }

    const objects: GeometryElement[] = [];

    // Resolve styles
    const lineStyle = resolveFeatureStyle('line', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Helper to get function params reactively
    const getParams = () => parentElement.getParams?.() ?? {};

    // Get the x position for first maximum
    const getFirstMaxX = () => {
      const p = getParams();
      const b = p['b'] ?? 1;
      const c = p['c'] ?? 0;
      if (parentElement.type === 'sine') {
        return c + Math.PI / (2 * b);
      } else {
        const a = p['a'] ?? 1;
        return a >= 0 ? c : c + Math.PI / b;
      }
    };

    // Get the x position for second maximum (one wavelength later)
    const getSecondMaxX = () => {
      const p = getParams();
      const b = p['b'] ?? 1;
      const period = (2 * Math.PI) / b;
      return getFirstMaxX() + period;
    };

    // Get the y position at maximum
    const getMaxY = () => {
      const p = getParams();
      return (p['d'] ?? 0) + Math.abs(p['a'] ?? 1);
    };

    // Create the wavelength arrow from first peak to second peak
    const wavelengthResult = elementFactory.line({
      a: [getFirstMaxX, getMaxY],
      b: [getSecondMaxX, getMaxY],
      name: 'λ',
      style: {
        ...lineStyle,
        visible,
        arrows: { first: true, last: true },
      },
    });
    objects.push(wavelengthResult.line);
    wavelengthResult.a.setAttribute({ visible: false });
    wavelengthResult.b.setAttribute({ visible: false });
    if (wavelengthResult.lineLabel) {
      objects.push(wavelengthResult.lineLabel);
    }

    return {
      id,
      type: 'wavelength',
      objects,
      getComputedValues: () => ({
        value: () => {
          const p = getParams();
          const b = p['b'] ?? 1;
          return (2 * Math.PI) / Math.abs(b);
        },
      }),
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
